import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  isValidDelay,
  sendsImmediately,
  toApiSteps,
  updateStepConfig,
} from "../../../../utils/automation-builder";
import FilterChips from "./FilterChips";
import { saveChanges } from "./save-changes";

const UNIT_OPTIONS = ["days", "hours"];
const MOVE_ICON = "/img/new-icons/drag-outline.svg";

/** The ⋯ menu of a step. */
export const StepMenu = ({ onEdit, onDuplicate, onDelete }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.stepMenu.${key}`);
  const option = (value, icon, danger) => ({
    text: (
      <span
        className={`ab-add-option${danger ? " ab-add-option--danger" : ""}`}
      >
        <span className="ab-add-option__icon">
          <SvgIcon
            src={`/img/new-icons/${icon}.svg`}
            useCurrentColor
            classNames="size-5 shrink-0"
            aria-hidden="true"
          />
        </span>
        {tr(value)}
      </span>
    ),
    value,
  });
  const options = [
    onEdit && option("edit", "edit-pencil"),
    onDuplicate && option("duplicate", "duplicate"),
    option("delete", "vuesax-linear-trash", true),
  ].filter(Boolean);
  const actions = { edit: onEdit, duplicate: onDuplicate, delete: onDelete };
  return (
    <Dropdown
      summaryText="···"
      options={options}
      onSelectFn={(action) => actions[action]()}
      overlay
      alignRight
    />
  );
};

StepMenu.propTypes = {
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
};

const CardTools = ({ children, draggable }) => (
  <span className="ab-tools">
    {draggable ? (
      <img src={MOVE_ICON} alt="" aria-hidden="true" className="ab-move" />
    ) : null}
    {children}
  </span>
);

CardTools.propTypes = {
  children: PropTypes.node,
  draggable: PropTypes.bool,
};

export const FilterCard = ({
  filters,
  fields,
  options,
  menu,
  draggable = true,
  href,
  selected,
}) => {
  const { t } = useTranslation("coaching-portal");
  const className = `ab-card ab-card--filter${selected ? " ab-card--selected" : ""}`;
  const content = (
    <>
      <header>
        <span className="ab-card__label">
          {t("emailAutomations.builder.filter.label")}
        </span>
        <CardTools draggable={draggable}>{menu}</CardTools>
      </header>
      <FilterChips filters={filters} fields={fields} options={options} />
    </>
  );
  // A Filter step opens its panel; the filter of the trigger has its own modal
  return href ? (
    <a className={className} href={href}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
};

FilterCard.propTypes = {
  filters: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  options: PropTypes.object,
  menu: PropTypes.node,
  draggable: PropTypes.bool,
  href: PropTypes.string,
  selected: PropTypes.bool,
};

export const EmailCard = ({
  steps,
  index,
  selected,
  menu,
  href,
  issueCodes = [],
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.email.${key}`);
  const { config } = steps[index];
  const filled = Boolean(config.subject?.trim());
  const tagKey = config.marketing ? "marketing" : "transactional";
  const missing = [
    issueCodes.includes("email_without_subject"),
    issueCodes.includes("email_without_body"),
  ];
  let problem = null;
  if (missing[0] && missing[1]) problem = "missingBoth";
  else if (missing[0]) problem = "missingSubject";
  else if (missing[1]) problem = "missingBody";
  return (
    <a
      className={`ab-card ab-card--email${selected ? " ab-card--selected" : ""}${problem ? " ab-card--error" : ""}`}
      href={href}
    >
      <header>
        <span className="ab-card__label">
          {tr("label")}
          {sendsImmediately(steps, index) ? ` · ${tr("immediately")}` : ""}
        </span>
        <span className="ab-card__kind">
          {problem ? tr(problem) : null}
          {!problem && filled ? t(`emailAutomations.tags.${tagKey}`) : null}
          {!problem && !filled ? tr("draft") : null}
        </span>
        <CardTools draggable>{menu}</CardTools>
      </header>
      <strong>{filled ? config.subject : tr("newEmail")}</strong>
      <span className="ab-card__body">
        {config.preheader?.trim() || (
          <span className="ab-card__cta">
            {tr("configure")}
            <SvgIcon
              src="/img/svg/arrow-right.svg"
              useCurrentColor
              classNames="size-4 shrink-0"
              aria-hidden="true"
            />
          </span>
        )}
      </span>
    </a>
  );
};

EmailCard.propTypes = {
  steps: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  menu: PropTypes.node,
  href: PropTypes.string.isRequired,
  issueCodes: PropTypes.arrayOf(PropTypes.string),
};

const delaySummary = (config, t) =>
  t(
    `emailAutomations.builder.delay.${config.unit === "hours" ? "summaryHours" : "summary"}`,
    { count: config.amount }
  );

// The delay as a pill; selected, it opens in place as "Wait for [3] [Days]"
export const DelayStep = ({
  automationId,
  steps,
  index,
  selected,
  menu,
  href,
  quickOptions,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.delay.${key}`);
  const { config } = steps[index];
  const [amount, setAmount] = useState(String(config.amount));
  const [unit, setUnit] = useState(config.unit);

  const pickQuick = (hours) => {
    setAmount(String(hours));
    setUnit("hours");
    save({ amount: hours, unit: "hours" });
  };

  const save = (next) => {
    const candidate = { amount: Number(next.amount), unit: next.unit };
    if (!isValidDelay(candidate)) return;
    if (candidate.amount === config.amount && candidate.unit === config.unit) {
      return;
    }
    saveChanges(automationId, {
      steps: toApiSteps(updateStepConfig(steps, index, candidate)),
    });
  };

  if (!selected) {
    return (
      <a className="ab-pill" href={href}>
        <SvgIcon
          src="/img/new-icons/vuesax-linear-clock.svg"
          useCurrentColor
          classNames="size-5 shrink-0"
          aria-hidden="true"
        />
        {delaySummary(config, t)}
      </a>
    );
  }
  return (
    <div className="ab-card ab-card--delay ab-card--selected">
      <header>
        <span className="ab-card__label">{tr("label")}</span>
        <CardTools draggable>{menu}</CardTools>
      </header>
      {quickOptions ? (
        <div className="ab-delay-quick">
          {[
            [1, "quick.oneHour"],
            [2, "quick.twoHours"],
          ].map(([hours, label]) => (
            <button
              key={hours}
              type="button"
              data-button-type="secondary"
              data-current={
                config.amount === hours && config.unit === "hours"
                  ? ""
                  : undefined
              }
              onClick={() => pickQuick(hours)}
            >
              {tr(label)}
            </button>
          ))}
        </div>
      ) : null}
      <div className="ab-delay-editor">
        <span>{tr("waitFor")}</span>
        <input
          type="number"
          min={1}
          value={amount}
          aria-label={tr("amount")}
          onChange={(event) => setAmount(event.target.value)}
          onBlur={() => save({ amount, unit })}
        />
        <div className="ep-select ab-delay-unit">
          <Dropdown
            overlay
            summaryText={tr(`units.${unit}`)}
            selectedValue={unit}
            options={UNIT_OPTIONS.map((value) => ({
              text: tr(`units.${value}`),
              value,
            }))}
            onSelectFn={(value) => {
              setUnit(value);
              save({ amount, unit: value });
            }}
          />
        </div>
      </div>
    </div>
  );
};

DelayStep.propTypes = {
  automationId: PropTypes.string.isRequired,
  steps: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  menu: PropTypes.node,
  href: PropTypes.string.isRequired,
  quickOptions: PropTypes.bool,
};
